import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Computer as ComputerIcon,
  PhoneAndroid as PhoneIcon,
  Tablet as TabletIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  ChevronLeft,
  ChevronRight,
  Warning as WarningIcon,
  WifiTethering as OnlineIcon,
  Today as TodayIcon,
  Devices as DevicesIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import { formatRelativeTime } from "../../utils/dateUtils";

// ── Helpers ──────────────────────────────────────────────────────────────────
const deviceIcon = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("mobile") || t.includes("phone")) return <PhoneIcon sx={{ fontSize: 16 }} />;
  if (t.includes("tablet")) return <TabletIcon sx={{ fontSize: 16 }} />;
  return <ComputerIcon sx={{ fontSize: 16 }} />;
};

const isAdmin = (rol) => rol === "admin" || rol === "administrador";

const roleChip = (rol) => (
  <Chip
    label={rol}
    size="small"
    sx={{
      bgcolor: isAdmin(rol) ? "rgba(255,193,7,0.15)" : "rgba(33,150,243,0.15)",
      color: isAdmin(rol) ? "#ffc107" : "#42a5f5",
      border: `1px solid ${isAdmin(rol) ? "#ffc10740" : "#42a5f540"}`,
      fontWeight: 700,
      fontSize: "0.68rem",
      height: 20,
    }}
  />
);

const HIST_PER_PAGE = 10;

const KPI_CONFIG = [
  { key: "en_linea",           label: "En línea ahora",        color: "#00e676", icon: <OnlineIcon   sx={{ fontSize: 28 }} /> },
  { key: "sesiones_hoy",       label: "Accesos hoy",           color: "#42a5f5", icon: <TodayIcon    sx={{ fontSize: 28 }} /> },
  { key: "total_dispositivos", label: "Dispositivos totales",  color: "#ab47bc", icon: <DevicesIcon  sx={{ fontSize: 28 }} /> },
  { key: "alertas",            label: "Alertas 24 h",          color: "#ff7043", icon: <SecurityIcon sx={{ fontSize: 28 }} /> },
];

// ── Componente principal ──────────────────────────────────────────────────────
export default function AdminSeguridadPanel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [histPage, setHistPage]   = useState(0);
  const [closingId, setClosingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin-security");
      if (res.data.success) {
        setData(res.data);
      } else {
        setError(res.data.message || "Error cargando datos de seguridad");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Error de conexión");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCloseSession = async (sessionId) => {
    setClosingId(sessionId);
    try {
      await api.delete("/admin-security", {
        data: { session_id: sessionId },
      });
      // Refrescar
      await fetchData();
    } catch (e) {
      /* silently refresh anyway */
      await fetchData();
    } finally {
      setClosingId(null);
    }
  };

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  const { kpis, sesiones_activas = [], historial = [], alertas_detalle = [] } = data;

  // Paginación historial
  const histTotal   = historial.length;
  const histStart   = histPage * HIST_PER_PAGE;
  const histSlice   = historial.slice(histStart, histStart + HIST_PER_PAGE);
  const histPages   = Math.ceil(histTotal / HIST_PER_PAGE);

  return (
    <Box>
      {/* Botón refrescar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Tooltip title="Actualizar datos">
          <IconButton onClick={fetchData} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── KPI Circles ─────────────────────────────────────────────────── */}
      <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
        {KPI_CONFIG.map(({ key, label, color, icon }) => (
          <Grid item key={key}>
            <Box
              sx={{
                width: { xs: 130, sm: 150 },
                height: { xs: 130, sm: 150 },
                borderRadius: "50%",
                background: `radial-gradient(circle at 35% 35%, ${color}35, ${color}12)`,
                border: `2px solid ${color}45`,
                boxShadow: `0 0 22px ${color}22`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                transition: "transform .2s, box-shadow .2s",
                "&:hover": { transform: "scale(1.06)", boxShadow: `0 0 32px ${color}45` },
              }}
            >
              <Box sx={{ color, opacity: 0.8 }}>{icon}</Box>
              <Typography
                sx={{
                  color,
                  fontWeight: 800,
                  fontSize: { xs: "1.6rem", sm: "1.8rem" },
                  lineHeight: 1,
                }}
              >
                {kpis[key]}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "rgba(255,255,255,0.6)",
                  fontWeight: 600,
                  textAlign: "center",
                  px: 1.5,
                  lineHeight: 1.2,
                }}
              >
                {label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* ── Alertas de seguridad ─────────────────────────────────────────── */}
      {alertas_detalle.length > 0 && (
        <Paper
          sx={{
            p: 2.5,
            mb: 3,
            border: "1px solid #ff704344",
            background: "rgba(255,112,67,0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WarningIcon sx={{ color: "#ff7043" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#ff7043" }}>
              Alertas de Seguridad
            </Typography>
            <Chip
              label={alertas_detalle.length}
              size="small"
              sx={{ bgcolor: "#ff704330", color: "#ff7043", fontWeight: 700 }}
            />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {alertas_detalle.map((alerta, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper
                  sx={{
                    p: 2,
                    background: "rgba(255,112,67,0.08)",
                    border: "1px solid #ff704330",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#ff7043", fontSize: "0.85rem" }}>
                      {(alerta.nombre_completo || alerta.username)?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", lineHeight: 1.2 }}>
                        {alerta.nombre_completo || alerta.username}
                      </Typography>
                      {roleChip(alerta.rol)}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)" }}>
                    <b style={{ color: "#ff7043" }}>{alerta.num_ips}</b> IPs distintas en 24 h
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: 0.5, wordBreak: "break-all" }}
                  >
                    {alerta.ips}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)", mt: 0.5 }}>
                    {alerta.total_sesiones} sesiones registradas
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── Sesiones Activas ─────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <OnlineIcon sx={{ color: "#00e676" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Sesiones Activas
          </Typography>
          <Chip
            label={sesiones_activas.length}
            size="small"
            sx={{ bgcolor: "#00e67620", color: "#00e676", fontWeight: 700 }}
          />
        </Box>
        <Divider sx={{ mb: 1 }} />

        {sesiones_activas.length === 0 ? (
          <Typography sx={{ py: 3, textAlign: "center", color: "rgba(255,255,255,0.5)" }}>
            Sin sesiones activas en este momento
          </Typography>
        ) : (
          sesiones_activas.map((s) => (
            <Accordion
              key={s.id}
              disableGutters
              sx={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "8px !important",
                mb: 1,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: isAdmin(s.rol) ? "#ffc107" : "#42a5f5",
                      color: "#000",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {(s.nombre_completo || s.username)?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  {/* Nombre + rol */}
                  <Box sx={{ minWidth: 0, flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", lineHeight: 1.2 }}>
                      {s.nombre_completo || s.username}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>
                      @{s.username}
                    </Typography>
                  </Box>

                  {roleChip(s.rol)}

                  {/* Dispositivo */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "0.8rem",
                      flexShrink: 0,
                    }}
                  >
                    {deviceIcon(s.device_type)}
                    <Typography sx={{ fontSize: "0.8rem" }}>{s.device_name || s.device_type || "Dispositivo"}</Typography>
                  </Box>

                  {/* IP */}
                  <Typography sx={{ fontSize: "0.76rem", color: "#42a5f5", flexShrink: 0 }}>
                    {s.ip}
                  </Typography>

                  {/* Última actividad */}
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: "rgba(255,255,255,0.4)",
                      ml: "auto",
                      flexShrink: 0,
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {formatRelativeTime ? formatRelativeTime(s.last_activity) : s.last_activity}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>Sistema</Typography>
                    <Typography sx={{ fontSize: "0.82rem" }}>{s.os || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>Navegador</Typography>
                    <Typography sx={{ fontSize: "0.82rem" }}>{s.browser || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>Inicio de sesión</Typography>
                    <Typography sx={{ fontSize: "0.82rem" }}>
                      {s.fecha_inicio ? new Date(s.fecha_inicio).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>Email</Typography>
                    <Typography sx={{ fontSize: "0.82rem", wordBreak: "break-all" }}>{s.email || "—"}</Typography>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={
                      closingId === s.id ? (
                        <CircularProgress size={14} color="error" />
                      ) : (
                        <BlockIcon />
                      )
                    }
                    disabled={closingId === s.id}
                    onClick={() => handleCloseSession(s.id)}
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Cerrar esta sesión
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>

      {/* ── Historial de Accesos ─────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <TodayIcon sx={{ color: "#42a5f5" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Historial de Accesos
          </Typography>
          <Chip
            label={`${histTotal} registros`}
            size="small"
            sx={{ bgcolor: "#42a5f520", color: "#42a5f5", fontWeight: 600 }}
          />
        </Box>
        <Divider sx={{ mb: 1 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", display: { xs: "none", sm: "table-cell" } }}>
                  Dispositivo
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", display: { xs: "none", md: "table-cell" } }}>
                  IP
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", textAlign: "center" }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {histSlice.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: "rgba(255,255,255,0.4)" }}>
                    Sin registros
                  </TableCell>
                </TableRow>
              ) : (
                histSlice.map((h) => (
                  <TableRow
                    key={h.id}
                    hover
                    sx={{ "&:hover": { background: "rgba(255,255,255,0.03)" } }}
                  >
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: isAdmin(h.rol) ? "#ffc107" : "#42a5f5",
                            color: "#000",
                            fontSize: "0.65rem",
                            fontWeight: 700,
                          }}
                        >
                          {(h.nombre_completo || h.username)?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.2 }}>
                            {h.nombre_completo || h.username}
                          </Typography>
                          <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.4)" }}>
                            @{h.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{roleChip(h.rol)}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", display: { xs: "none", sm: "table-cell" } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "rgba(255,255,255,0.7)" }}>
                        {deviceIcon(h.device_type)}
                        {h.device_name || h.device_type || "—"}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "0.75rem", color: "#42a5f5", display: { xs: "none", md: "table-cell" } }}
                    >
                      {h.ip || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)" }}>
                      {h.fecha_inicio
                        ? new Date(h.fecha_inicio).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Chip
                        label={h.is_active ? "Activa" : "Cerrada"}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          bgcolor: h.is_active ? "#00e67620" : "rgba(255,255,255,0.07)",
                          color: h.is_active ? "#00e676" : "rgba(255,255,255,0.4)",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación historial */}
        {histPages > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 2 }}>
            <IconButton
              size="small"
              disabled={histPage === 0}
              onClick={() => setHistPage((p) => p - 1)}
            >
              <ChevronLeft />
            </IconButton>
            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)" }}>
              {histStart + 1}–{Math.min(histStart + HIST_PER_PAGE, histTotal)} de {histTotal}
            </Typography>
            <IconButton
              size="small"
              disabled={histPage >= histPages - 1}
              onClick={() => setHistPage((p) => p + 1)}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
